import { useEffect, useState, useCallback } from "react";
import {
  Zap, Bell, Calendar, Plus, Pencil, LogOut, Home, Sparkles, Settings, Share2, Users
} from "lucide-react";
import api, { clearAuth } from "../lib/api";
import NewsManager from "./NewsManager";
import SettingsDialog from "./SettingsDialog";
import InvitesDialog from "./InvitesDialog";
import ZapPicker from "./ZapPicker";
import ZapWizard from "./ZapWizard";
import EventCreator from "./EventCreator";
import EventsList from "./EventsList";
import EventDetailDialog from "./EventDetailDialog";
import MembersDialog from "./MembersDialog";
import { toast } from "sonner";

function CalendlyEmbed({ url }) {
  useEffect(() => {
    const id = "calendly-widget-script";
    if (!document.getElementById(id) && url) {
      const s = document.createElement("script");
      s.id = id;
      s.src = "https://assets.calendly.com/assets/external/widget.js";
      s.async = true;
      document.body.appendChild(s);
    }
  }, [url]);

  if (!url) return null;
  return (
    <div
      data-testid="calendly-widget"
      className="calendly-inline-widget"
      data-url={url}
      style={{ minWidth: "320px", height: "640px" }}
    />
  );
}

function formatDateIT(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function Dashboard({ onLogout, currentUser }) {
  const [news, setNews] = useState([]);
  const [family, setFamily] = useState(null);
  const [loadingNews, setLoadingNews] = useState(true);
  const [managerOpen, setManagerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [zapOpen, setZapOpen] = useState(false);
  const [zapWizardOpen, setZapWizardOpen] = useState(false);
  const [invitesOpen, setInvitesOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [eventCreatorOpen, setEventCreatorOpen] = useState(false);
  const [eventDetailId, setEventDetailId] = useState(null);

  const loadNews = useCallback(async () => {
    setLoadingNews(true);
    try {
      const res = await api.get("/news");
      setNews(res.data);
    } catch (err) {
      toast.error("Impossibile caricare le news");
    } finally {
      setLoadingNews(false);
    }
  }, []);

  const loadFamily = useCallback(async () => {
    try {
      const res = await api.get("/family/me");
      setFamily(res.data);
    } catch (err) {
      console.error("family load error", err);
    }
  }, []);

  const loadEvents = useCallback(async () => {
    setLoadingEvents(true);
    try {
      const res = await api.get("/events");
      setEvents(res.data);
    } catch (err) {
      console.error("events load error", err);
    } finally {
      setLoadingEvents(false);
    }
  }, []);

  useEffect(() => {
    loadNews();
    loadFamily();
    loadEvents();
  }, [loadNews, loadFamily, loadEvents]);

  const openCreate = () => { setEditing(null); setManagerOpen(true); };
  const openEdit = (item) => { setEditing(item); setManagerOpen(true); };

  const handleLogout = async () => {
    try { await api.post("/auth/logout"); } catch (err) { console.warn("Logout API call failed", err); }
    clearAuth();
    toast("Arrivederci 👋");
    onLogout();
  };

  return (
    <div data-testid="dashboard" className="min-h-screen bg-[#FBF9F6]">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-[#E8E1D9] bg-white/80 backdrop-blur-xl">
        <div className="mx-auto max-w-3xl px-4 py-4 sm:px-6">
          {/* Top brand row */}
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img
                src="/logo-zap.png"
                alt="EASY Family"
                className="h-9 w-9 object-contain"
              />
              <div className="leading-tight">
                <p className="font-display text-base font-black tracking-tight text-[#2D2926]">
                  EASY <span className="italic font-light text-[#FF7C43]">Family</span>
                </p>
                <p className="font-display text-[11px] italic font-medium text-[#C87941] -mt-0.5">
                  Facciamo uno <span className="not-italic font-black text-[#FF7C43]">ZAP?</span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="members-button"
                onClick={() => setMembersOpen(true)}
                title="Membri"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-[#E8E1D9] bg-white text-[#5C5855] transition hover:border-[#FF7C43] hover:text-[#FF7C43]"
              >
                <Users className="h-3.5 w-3.5" />
              </button>
              <button
                data-testid="invites-button"
                onClick={() => setInvitesOpen(true)}
                title="Inviti"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-[#E8E1D9] bg-white text-[#5C5855] transition hover:border-[#FF7C43] hover:text-[#FF7C43]"
              >
                <Share2 className="h-3.5 w-3.5" />
              </button>
              <button
                data-testid="settings-button"
                onClick={() => setSettingsOpen(true)}
                title="Impostazioni"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-[#E8E1D9] bg-white text-[#5C5855] transition hover:border-[#FF7C43] hover:text-[#FF7C43]"
              >
                <Settings className="h-3.5 w-3.5" />
              </button>
              <button
                data-testid="logout-button"
                onClick={handleLogout}
                title="Esci"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-[#E8E1D9] bg-white text-[#5C5855] transition hover:border-[#FF7C43] hover:text-[#FF7C43]"
              >
                <LogOut className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Casa identity row */}
          <div className="flex items-center gap-3 border-t border-[#E8E1D9] pt-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#FBF9F6] border border-[#E8E1D9]">
              <Home className="h-5 w-5 text-[#C87941]" strokeWidth={2.5} />
            </div>
            <div>
              <h1
                data-testid="house-title"
                className="font-display text-2xl sm:text-3xl font-black leading-none tracking-tight text-[#2D2926]"
              >
                {family?.name || "Casa"}
              </h1>
              <div className="mt-1 flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-green-500 pulse-dot" />
                <span
                  data-testid="rotta-indicator"
                  className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#C87941]"
                >
                  Rotta Familiare Attiva
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 pb-32 pt-8 sm:px-6 space-y-8">
        {/* Welcome */}
        <section className="animate-fade">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#E8E1D9] bg-white px-3 py-1">
            <Sparkles className="h-3 w-3 text-[#FF7C43]" />
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C87941]">
              Benvenuta a casa
            </span>
          </div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black leading-tight tracking-tight text-[#2D2926]">
            Cosa bolle in <span className="italic font-light text-[#FF7C43]">pentola</span> oggi?
          </h2>
          <p className="mt-2 max-w-xl text-base text-[#5C5855]">
            Aggiornamenti freschi dalla famiglia, prossimi ZAP in calendario e un click per organizzarsi.
          </p>
        </section>

        {/* Hero CTA: Crea uno ZAP */}
        <section className="animate-fade">
          <button
            data-testid="create-event-cta"
            onClick={() => setEventCreatorOpen(true)}
            className="group relative w-full overflow-hidden rounded-3xl bg-gradient-to-br from-red-600 via-red-500 to-[#FF7C43] p-6 text-left text-white shadow-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl active:scale-[0.98] sm:p-8"
          >
            {/* Decorative grain */}
            <div className="pointer-events-none absolute inset-0 opacity-10" style={{
              backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
            }} />
            <div className="relative flex items-center gap-4">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-md group-hover:bg-white/30">
                <img src="/logo-zap.png" alt="" className="h-10 w-10 object-contain drop-shadow" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/80">
                  Organizza un Ritrovo
                </p>
                <h3 className="mt-1 font-display text-2xl sm:text-3xl font-black tracking-tight">
                  Crea uno ZAP
                </h3>
                <p className="mt-1 text-sm text-white/90">
                  Proponi 2–4 date, gli invitati votano la preferita.
                </p>
              </div>
              <div className="hidden shrink-0 sm:block">
                <span className="rounded-full bg-white/20 px-3 py-1 text-[10px] font-bold uppercase tracking-wider backdrop-blur-md">
                  Min 14 giorni
                </span>
              </div>
            </div>
          </button>
        </section>

        {/* Events Section */}
        <section data-testid="events-section" className="animate-fade">
          <div className="mb-4">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-[#FF7C43]" fill="currentColor" />
              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C87941]">
                I tuoi ZAP
              </span>
            </div>
            <h3 className="mt-1 font-display text-2xl font-bold tracking-tight text-[#2D2926]">
              Ritrovi in programma
            </h3>
          </div>
          <EventsList
            events={events}
            loading={loadingEvents}
            onOpen={(id) => setEventDetailId(id)}
          />
        </section>

        {/* News Section */}
        <section data-testid="news-section" className="animate-fade">
          <div className="mb-4 flex items-end justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-[#FF7C43]" />
                <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C87941]">
                  Ultime News
                </span>
              </div>
              <h3 className="mt-1 font-display text-2xl font-bold tracking-tight text-[#2D2926]">
                Bacheca di Famiglia
              </h3>
            </div>
            <button
              data-testid="add-news-button"
              onClick={openCreate}
              className="flex items-center gap-2 rounded-full bg-[#2D2926] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#FF7C43] active:scale-95"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Aggiungi</span>
            </button>
          </div>

          {loadingNews ? (
            <div className="grid gap-4 sm:grid-cols-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-28 animate-pulse rounded-3xl bg-white border border-[#E8E1D9]" />
              ))}
            </div>
          ) : news.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-[#E8E1D9] bg-white p-10 text-center">
              <p className="text-[#5C5855]">Nessuna news. Aggiungine una per iniziare.</p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {news.map((item, idx) => {
                const isPrimary = item.type === "primary";
                return (
                  <article
                    key={item.id}
                    data-testid={`news-card-${idx}`}
                    onClick={() => openEdit(item)}
                    className={`group relative cursor-pointer overflow-hidden rounded-3xl border bg-white p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-md ${
                      isPrimary
                        ? "border-[#FF7C43]/40 hover:border-[#FF7C43]"
                        : "border-[#E8E1D9] hover:border-[#C87941]"
                    }`}
                  >
                    <div className={`absolute left-0 top-0 h-full w-1.5 ${isPrimary ? "bg-[#FF7C43]" : "bg-[#C87941]/40"}`} />
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.2em] ${
                          isPrimary ? "bg-[#FF7C43]/10 text-[#FF7C43]" : "bg-[#C87941]/10 text-[#C87941]"
                        }`}>
                          {isPrimary ? "Importante" : "News"}
                        </span>
                        <h4 className="mt-2 font-display text-lg font-bold leading-snug text-[#2D2926] line-clamp-2">
                          {item.title}
                        </h4>
                        {item.body && (
                          <p className="mt-1.5 text-sm text-[#5C5855] line-clamp-2">{item.body}</p>
                        )}
                        <p className="mt-3 text-[10px] font-semibold uppercase tracking-widest text-[#C87941]/70">
                          {formatDateIT(item.created_at)}
                        </p>
                      </div>
                      <Pencil className="h-4 w-4 flex-shrink-0 text-[#C87941] opacity-0 transition group-hover:opacity-100" />
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        {/* Calendly Section */}
        <section data-testid="calendly-section" className="animate-fade">
          <div className="mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-[#FF7C43]" />
              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C87941]">
                Programma uno ZAP
              </span>
            </div>
            <h3 className="mt-1 font-display text-2xl font-bold tracking-tight text-[#2D2926]">
              Quando facciamo uno ZAP?
            </h3>
            <p className="mt-1 text-sm text-[#5C5855]">
              Scegli il giorno, ci pensiamo noi al resto.
            </p>
          </div>
          <div className="overflow-hidden rounded-3xl border border-[#E8E1D9] bg-white">
            <CalendlyEmbed url={family?.calendly_url} />
          </div>
        </section>
      </main>

      {/* Floating ZAP rapido — moved to bottom-left to avoid Emergent badge overlap */}
      <button
        data-testid="zap-floating-button"
        onClick={() => setZapOpen(true)}
        className="fixed bottom-6 left-6 z-[9999] flex items-center gap-2 rounded-full bg-white border border-[#E8E1D9] px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#5C5855] shadow-md transition-all duration-300 hover:scale-105 active:scale-95"
        title="Saluto rapido WhatsApp"
      >
        <Zap className="h-3.5 w-3.5" fill="#FF7C43" stroke="#FF7C43" />
        <span>ZAP rapido</span>
      </button>

      {/* Main ZAP — calendar-based */}
      <button
        data-testid="zap-wizard-button"
        onClick={() => setZapWizardOpen(true)}
        className="fixed bottom-6 right-6 z-[9999] flex items-center gap-2 rounded-full papaya-gradient px-6 py-4 text-sm font-bold uppercase tracking-wider text-white tuscany-glow transition-all duration-300 hover:scale-105 active:scale-95"
        title="Trova quando siete tutti liberi"
      >
        <Zap className="h-5 w-5" fill="white" />
        <span>Facciamo uno ZAP</span>
      </button>

      <NewsManager
        open={managerOpen}
        onClose={() => setManagerOpen(false)}
        onSaved={loadNews}
        editing={editing}
      />

      <SettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        family={family}
        onSaved={(updated) => setFamily(updated)}
      />

      <ZapPicker
        open={zapOpen}
        onClose={() => setZapOpen(false)}
        family={family}
      />

      <ZapWizard
        open={zapWizardOpen}
        onClose={() => setZapWizardOpen(false)}
        currentUser={currentUser}
      />

      <InvitesDialog
        open={invitesOpen}
        onClose={() => setInvitesOpen(false)}
      />

      <MembersDialog
        open={membersOpen}
        onClose={() => setMembersOpen(false)}
        currentUser={currentUser}
      />

      <EventCreator
        open={eventCreatorOpen}
        onClose={() => setEventCreatorOpen(false)}
        onCreated={() => { loadEvents(); }}
      />

      <EventDetailDialog
        open={Boolean(eventDetailId)}
        onClose={() => setEventDetailId(null)}
        eventId={eventDetailId}
        onChanged={loadEvents}
      />
    </div>
  );
}
